(function () {
  const dependencyPromises = {};
  const embeddedSessions = new Set();

  function preserveEmbeddedPage(root) {
    if (embeddedSessions.has(root)) return function () {};
    embeddedSessions.add(root);
    document.body.classList.add("zoom-experience-session-active");
    const viewport = document.querySelector('meta[name="viewport"]');
    const content = viewport ? viewport.getAttribute("content") : null;
    const observer = viewport ? new MutationObserver(function () {
      if (viewport.getAttribute("content") === content) return;
      if (content === null) viewport.removeAttribute("content");
      else viewport.setAttribute("content", content);
    }) : null;
    if (observer) observer.observe(viewport, { attributes: true, attributeFilter: ["content"] });

    return function () {
      if (observer) observer.disconnect();
      if (viewport) {
        if (content === null) viewport.removeAttribute("content");
        else viewport.setAttribute("content", content);
      }
      embeddedSessions.delete(root);
      if (!embeddedSessions.size) document.body.classList.remove("zoom-experience-session-active");
    };
  }

  function loadScript(url, globalName) {
    if (window[globalName]) return Promise.resolve();
    if (dependencyPromises[url]) return dependencyPromises[url];
    dependencyPromises[url] = new Promise(function (resolve, reject) {
      const existing = document.querySelector('script[src="' + url + '"]');
      const script = existing || document.createElement("script");
      const onLoad = function () {
        if (window[globalName]) resolve();
        else reject(new Error(globalName + " was not exposed by the loaded SDK"));
      };
      script.addEventListener("load", onLoad, { once: true });
      script.addEventListener("error", function () {
        reject(new Error("Unable to load the Zoom SDK"));
      }, { once: true });
      if (!existing) {
        script.src = url;
        script.async = true;
        document.head.appendChild(script);
      }
    });
    return dependencyPromises[url];
  }

  function loadStylesheet(url) {
    if (document.querySelector('link[href="' + url + '"]')) return;
    const stylesheet = document.createElement("link");
    stylesheet.rel = "stylesheet";
    stylesheet.href = url;
    document.head.appendChild(stylesheet);
  }

  function loadDependencies(mode) {
    if (mode === "broadcast_viewer") {
      return loadScript("https://source.zoom.us/videosdk/zoom-streaming-2.5.0.min.js", "WebBroadcastStreaming");
    }
    loadStylesheet("https://source.zoom.us/uitoolkit/2.5.0-1/videosdk-ui-toolkit.css");
    return loadScript("https://source.zoom.us/uitoolkit/2.5.0-1/videosdk-ui-toolkit.min.umd.js", "UIToolkit");
  }

  function parseLabels(root) {
    const node = root.querySelector("[data-zoom-labels]");
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return {};
    }
  }

  function setStatus(root, message, isError) {
    const status = root.querySelector("[data-status]");
    if (!status) return;
    status.textContent = message;
    status.classList.toggle("zoom-experience__status--error", Boolean(isError));
  }

  function errorMessage(error) {
    if (error instanceof Error && error.message) return error.message;
    if (typeof error === "string") return error;
    if (error && typeof error.reason === "string") return error.reason;
    if (error && typeof error.message === "string") return error.message;
    return "Unknown error";
  }

  async function requestToken(root, values, labels) {
    const endpoint = root.dataset.authEndpoint;
    if (!endpoint) throw new Error(labels.requestFailed || "Token endpoint is missing");
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set("sessionName", values.sessionName);
    url.searchParams.set("userName", values.userName);
    url.searchParams.set("roleType", String(values.roleType));
    url.searchParams.set("mode", values.mode);
    if (values.hostKey) url.searchParams.set("hostKey", values.hostKey);
    if (values.channelId) url.searchParams.set("channelId", values.channelId);
    if (values.mode === "broadcast_viewer" && !values.channelId) {
      url.searchParams.set("requestChannel", "true");
    }

    const response = await fetch(url.toString(), {
      headers: { Accept: "application/json" },
      method: "GET"
    });
    let data = {};
    try {
      data = await response.json();
    } catch (error) {
      data = {};
    }
    if (!response.ok) throw new Error(data.error || data.message || labels.requestFailed || "Token request failed");
    const token = data.broadcastToken || data.token || data.signature;
    if (!token) throw new Error(labels.requestFailed || "Token response did not include a token");
    return {
      channelId: data.channelId || data.broadcastChannelId || data.channel || (data.broadcast && data.broadcast.channelId) || "",
      token: token,
      sessionName: data.sessionName || values.sessionName
    };
  }

  function sessionConfig(token, sessionName, userName) {
    return {
      debug: true,
      leaveOnPageUnload: true,
      sessionName: sessionName,
      userName: userName,
      videoSDKJWT: token,
      featuresOptions: {
        audio: { enable: true, joinAudioConsent: false },
        chat: { enable: false },
        footer: { enable: true },
        header: { enable: true },
        invite: { enable: true },
        leave: { enable: true },
        preview: { enable: true, isAllowModifyName: false },
        settings: { enable: true },
        share: { enable: true },
        theme: { enable: true },
        users: { enable: true },
        video: { enable: true },
        viewMode: {
          enable: true,
          defaultViewMode: "speaker",
          viewModes: ["speaker", "gallery"]
        }
      }
    };
  }

  function serviceEndpoint(root, endpointName) {
    const url = new URL(root.dataset.authEndpoint, window.location.origin);
    url.pathname = url.pathname.replace(/\/[^/]*\/?$/, "/" + endpointName);
    url.search = "";
    url.hash = "";
    return url.toString();
  }

  function broadcastChannelEndpoint(root) {
    return serviceEndpoint(root, "zoom-broadcast-channel");
  }

  function populateViewerQuery(root) {
    if (root.dataset.mode !== "broadcast_viewer" || (root.dataset.channelId || "").trim()) return;
    const params = new URLSearchParams(window.location.search);
    const session = params.get("session") || params.get("sessionName");
    const channel = params.get("channel") || params.get("channelId");
    if (session && channel) root.dataset.sessionName = session;
    if (channel) root.dataset.channelId = channel;
  }

  function initializeSession(root, labels) {
    const toolkit = window.UIToolkit;
    const form = root.querySelector("[data-session-form]");
    const container = root.querySelector("[data-session-container]");
    const isHost = root.dataset.mode === "broadcast_host";
    let joined = false;
    let broadcastClient = null;
    let zoomClient = null;
    let broadcastActionInFlight = false;
    let activeHostKey = "";
    let activeSessionName = root.dataset.sessionName || "shopify-room";
    let restoreEmbeddedPage = null;
    let removeSessionListeners = null;

    if (!toolkit || !form || !container) {
      setStatus(root, labels.sdkUnavailable || "Zoom SDK could not be loaded", true);
      return;
    }

    let videoTouch = null;
    container.addEventListener("touchstart", function (event) {
      const target = event.target instanceof Element ? event.target : null;
      videoTouch = event.touches.length === 1 && target && target.closest(".uikit-main-content")
        && !target.closest('button, input, select, textarea, a, [contenteditable="true"]')
        ? { x: event.touches[0].clientX, y: event.touches[0].clientY, lastY: event.touches[0].clientY }
        : null;
    }, { capture: true, passive: true });
    container.addEventListener("touchmove", function (event) {
      if (!videoTouch || event.touches.length !== 1) {
        videoTouch = null;
        return;
      }
      const touch = event.touches[0];
      const deltaX = Math.abs(touch.clientX - videoTouch.x);
      const deltaY = Math.abs(touch.clientY - videoTouch.y);
      if (deltaY > 8 && deltaY > deltaX && event.cancelable) {
        window.scrollBy(0, videoTouch.lastY - touch.clientY);
        event.preventDefault();
        event.stopPropagation();
      }
      videoTouch.lastY = touch.clientY;
    }, { capture: true, passive: false });
    function clearVideoTouch() { videoTouch = null; }
    container.addEventListener("touchend", clearVideoTouch, { passive: true });
    container.addEventListener("touchcancel", clearVideoTouch, { passive: true });

    const broadcastDetails = root.querySelector("[data-broadcast-details]");
    const viewerLinkGroup = root.querySelector("[data-viewer-link-group]");
    const viewerLink = root.querySelector("[data-viewer-link]");
    function setViewerLink(channelId) {
      if (!viewerLink || !viewerLinkGroup || !channelId) return;
      const url = new URL(root.dataset.viewerUrl || "/pages/zoom-broadcast-viewer", window.location.origin);
      url.searchParams.set("channel", channelId);
      url.searchParams.set("session", activeSessionName);
      viewerLink.value = url.toString();
      viewerLinkGroup.hidden = false;
    }

    function clearViewerLink() {
      if (viewerLink) viewerLink.value = "";
      if (viewerLinkGroup) viewerLinkGroup.hidden = true;
    }

    async function syncActiveBroadcastChannel(channelId) {
      if (!activeHostKey) return;
      const response = await fetch(broadcastChannelEndpoint(root), {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          channelId: channelId || "",
          hostKey: activeHostKey,
          sessionName: activeSessionName
        })
      });
      if (!response.ok) {
        let data = {};
        try {
          data = await response.json();
        } catch (error) {
          data = {};
        }
        throw new Error(data.error || data.message || "Unable to update the active broadcast channel");
      }
    }

    async function initializeBroadcastDetails() {
      if (!isHost || broadcastClient) return;
      zoomClient = toolkit.getClient();
      if (!zoomClient || typeof zoomClient.getBroadcastStreamingClient !== "function") {
        setStatus(root, labels.broadcastUnavailable || "Broadcast streaming is unavailable", true);
        return;
      }
      broadcastClient = zoomClient.getBroadcastStreamingClient();
      const enabled = await Promise.resolve(broadcastClient.isBroadcastStreamingEnable());
      if (!enabled) {
        setStatus(root, labels.broadcastUnavailable || "Broadcast streaming is unavailable", true);
        return;
      }
      if (broadcastDetails) broadcastDetails.hidden = false;
      setStatus(root, labels.broadcastReady || "Ready to broadcast");

      const handleBroadcastStatus = function (payload) {
        const eventStatus = payload && payload.status !== undefined ? Number(payload.status) : NaN;
        if (broadcastClient && typeof broadcastClient.getBroadcastStreamingStatus === "function") {
          const current = broadcastClient.getBroadcastStreamingStatus();
          const status = current && current.status !== undefined ? Number(current.status) : eventStatus;
          const channelId = current && current.channelId ? String(current.channelId) : "";
          const statusLabels = {
            0: "Not started",
            1: "Starting",
            2: "Live",
            3: "Stopping",
            4: "Stopped"
          };
          setStatus(root, (labels.broadcastStatus || "Broadcast status: ") + (statusLabels[status] || String(status)));

          if (status === 2 && channelId) {
            setViewerLink(channelId);
            syncActiveBroadcastChannel(channelId).catch(function (error) {
              setStatus(root, errorMessage(error), true);
            });
          } else if (status === 3 || status === 4 || status === 0) {
            clearViewerLink();
            syncActiveBroadcastChannel("").catch(function (error) {
              setStatus(root, errorMessage(error), true);
            });
          }
        }
      };

      const runBroadcastAction = async function (shouldStart) {
        if (broadcastActionInFlight || !broadcastClient) return;
        broadcastActionInFlight = true;
        setStatus(root, (labels.broadcastStatus || "Broadcast status: ") + (shouldStart ? "Starting" : "Stopping"));

        try {
          const current = typeof broadcastClient.getBroadcastStreamingStatus === "function"
            ? broadcastClient.getBroadcastStreamingStatus()
            : null;
          const currentStatus = current && current.status !== undefined ? Number(current.status) : NaN;

          if (shouldStart) {
            if (currentStatus === 2) {
              handleBroadcastStatus({ status: currentStatus });
              return;
            }
            await broadcastClient.startBroadcast();
          } else {
            const channelId = current && current.channelId ? String(current.channelId) : "";
            await broadcastClient.stopBroadcast(channelId || undefined);
          }

          handleBroadcastStatus({});
        } catch (error) {
          setStatus(root, errorMessage(error), true);
        } finally {
          broadcastActionInFlight = false;
        }
      };

      container.addEventListener("click", function (event) {
        const target = event.target instanceof Element ? event.target : null;
        const button = target ? target.closest("button") : null;
        if (!button || !container.contains(button)) return;
        const label = String(button.getAttribute("aria-label") || button.textContent || "")
          .replace(/\s+/g, " ")
          .trim()
          .toLowerCase();
        if (label !== "start broadcast" && label !== "stop broadcast") return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const shouldStart = label === "start broadcast";
        const moreButton = container.querySelector("#footer-more-button");
        window.setTimeout(function () {
          if (moreButton instanceof HTMLElement) moreButton.click();
        }, 0);
        runBroadcastAction(shouldStart);
      }, true);

      zoomClient.on("broadcast-streaming-status", handleBroadcastStatus);
      handleBroadcastStatus({});
    }

    function releaseEmbeddedPage() {
      if (removeSessionListeners) removeSessionListeners();
      removeSessionListeners = null;
      if (restoreEmbeddedPage) restoreEmbeddedPage();
      restoreEmbeddedPage = null;
    }

    document.addEventListener("shopify:section:unload", function (event) {
      if (event.target === root || event.target.contains(root)) releaseEmbeddedPage();
    });
    window.addEventListener("pagehide", releaseEmbeddedPage);

    form.addEventListener("submit", async function (event) {
      event.preventDefault();
      if (joined) return;
      const submitButton = form.querySelector('button[type="submit"]');
      const formData = new FormData(form);
      const sessionName = String(formData.get("session") || "").trim();
      const userName = String(formData.get("userName") || "").trim();
      const hostKey = String(formData.get("hostKey") || "").trim();
      if (!userName) {
        setStatus(root, labels.nameRequired || "Enter your name to join.", true);
        return;
      }
      if (!sessionName || (isHost && !hostKey)) return;
      activeSessionName = sessionName;
      activeHostKey = hostKey;
      submitButton.disabled = true;
      setStatus(root, labels.requestingToken || "Requesting access token");

      try {
        const auth = await requestToken(root, {
          hostKey: hostKey,
          mode: isHost ? "broadcast_host" : "one_to_one_viewer",
          roleType: isHost ? 1 : 0,
          sessionName: sessionName,
          userName: userName
        }, labels);
        activeSessionName = auth.sessionName;
        setStatus(root, labels.joiningSession || "Joining session");
        const onJoined = function () {
          if (joined || !restoreEmbeddedPage) return;
          joined = true;
          form.hidden = true;
          setStatus(root, labels.sessionJoined || "Session joined");
          initializeBroadcastDetails().catch(function (error) {
            setStatus(root, errorMessage(error), true);
          });
        };
        const onClosed = function () {
          releaseEmbeddedPage();
          joined = false;
          form.hidden = false;
          submitButton.disabled = false;
          if (broadcastDetails) broadcastDetails.hidden = true;
          clearViewerLink();
          if (isHost && activeHostKey) {
            syncActiveBroadcastChannel("").catch(function () {});
          }
          setStatus(root, labels.sessionClosed || "Session closed");
        };
        releaseEmbeddedPage();
        restoreEmbeddedPage = preserveEmbeddedPage(root);
        const joinPromise = toolkit.joinSession(
          container,
          sessionConfig(auth.token, auth.sessionName, userName)
        );
        toolkit.onSessionJoined(onJoined);
        toolkit.onSessionClosed(onClosed);
        removeSessionListeners = function () {
          toolkit.offSessionJoined(onJoined);
          toolkit.offSessionClosed(onClosed);
        };
        await joinPromise;
      } catch (error) {
        releaseEmbeddedPage();
        submitButton.disabled = false;
        setStatus(root, errorMessage(error), true);
      }
    });

    const copyButton = root.querySelector("[data-copy-viewer-link]");
    if (copyButton && viewerLink) {
      copyButton.addEventListener("click", async function () {
        try {
          await navigator.clipboard.writeText(viewerLink.value);
          setStatus(root, labels.linkCopied || "Viewer link copied");
        } catch (error) {
          viewerLink.focus();
          viewerLink.select();
          setStatus(root, labels.copyFailed || "Copy the selected link", true);
        }
      });
    }
  }

  async function initializeBroadcastViewer(root, labels) {
    const namespace = window.WebBroadcastStreaming;
    const player = root.querySelector("[data-broadcast-player]");
    if (!namespace || !namespace.default || !player) {
      setStatus(root, labels.sdkUnavailable || "Zoom SDK could not be loaded", true);
      return;
    }

    const randomValue = window.crypto && window.crypto.getRandomValues
      ? window.crypto.getRandomValues(new Uint32Array(1))[0]
      : Math.floor(Math.random() * 4294967296);
    const viewerName = (labels.broadcastViewerName || "Broadcast viewer") + " " + randomValue.toString(16).padStart(8, "0").toUpperCase();
    root.dataset.userName = viewerName;
    const streaming = namespace.default.createClient();
    let activeChannelId = "";
    let disposed = false;
    let audioNeedsInteraction = false;

    const restoreAudio = function () {
      const liveVideo = player.querySelector("live-video");
      if (liveVideo) {
        liveVideo.setAttribute("muted", "false");
        audioNeedsInteraction = false;
        setStatus(root, labels.broadcastConnected || "Connected to broadcast");
      }
      document.removeEventListener("click", restoreAudio);
      document.removeEventListener("touchstart", restoreAudio);
    };
    const cleanup = function () {
      disposed = true;
      document.removeEventListener("click", restoreAudio);
      document.removeEventListener("touchstart", restoreAudio);
      document.removeEventListener("shopify:section:unload", onSectionUnload);
      window.removeEventListener("pagehide", cleanup);
      if (activeChannelId) {
        const channelId = activeChannelId;
        activeChannelId = "";
        Promise.resolve().then(function () { return streaming.detachStreaming(channelId); }).catch(function () {});
      }
    };
    const onSectionUnload = function (event) {
      if (event.target === root || event.target.contains(root)) cleanup();
    };
    window.addEventListener("pagehide", cleanup, { once: true });
    document.addEventListener("shopify:section:unload", onSectionUnload);

    streaming.on("connection-change", function (payload) {
      if (disposed) return;
      if (payload && payload.state === "Connected" && !audioNeedsInteraction) {
        setStatus(root, labels.broadcastConnected || "Connected to broadcast");
      } else if (payload && payload.state === "Fail") {
        setStatus(root, labels.requestFailed || "Unable to connect", true);
      }
    });
    streaming.on("auto-play-audio-failed", function () {
      if (disposed) return;
      audioNeedsInteraction = true;
      setStatus(root, labels.audioInteractionRequired || "Tap anywhere on the page to enable audio.");
      document.addEventListener("click", restoreAudio, { once: true });
      document.addEventListener("touchstart", restoreAudio, { once: true });
    });

    setStatus(root, labels.requestingToken || "Requesting access token");
    try {
      const channelId = String(root.dataset.channelId || "").trim();
      const auth = await requestToken(root, {
        channelId: channelId,
        mode: "broadcast_viewer",
        roleType: 0,
        sessionName: String(root.dataset.sessionName || "shopify-room").trim(),
        userName: viewerName
      }, labels);
      if (disposed) return;
      const resolvedChannelId = channelId || auth.channelId;
      if (!resolvedChannelId) {
        throw new Error(labels.missingChannel || "No active broadcast channel was returned by the server");
      }
      setStatus(root, labels.connectingBroadcast || "Connecting to broadcast");
      const quality = namespace.VideoQuality && namespace.VideoQuality.Video_720P !== undefined
        ? namespace.VideoQuality.Video_720P
        : 3;
      const element = await streaming.attachStreaming(resolvedChannelId, auth.token, quality);
      activeChannelId = resolvedChannelId;
      if (disposed) {
        cleanup();
        return;
      }
      element.setAttribute("playsinline", "true");
      element.addEventListener("ended", function () {
        setStatus(root, labels.broadcastEnded || "Broadcast ended");
        cleanup();
      });
      player.replaceChildren(element);
      setStatus(root, audioNeedsInteraction
        ? labels.audioInteractionRequired || "Tap anywhere on the page to enable audio."
        : labels.broadcastConnected || "Connected to broadcast");
    } catch (error) {
      if (!disposed) setStatus(root, errorMessage(error), true);
      cleanup();
    }
  }

  async function initialize(root) {
    if (!root || root.dataset.zoomInitialized === "true") return;
    root.dataset.zoomInitialized = "true";
    const labels = parseLabels(root);
    populateViewerQuery(root);
    try {
      await loadDependencies(root.dataset.mode);
      if (root.dataset.mode === "broadcast_viewer") {
        await initializeBroadcastViewer(root, labels);
      } else {
        initializeSession(root, labels);
      }
    } catch (error) {
      setStatus(root, labels.sdkUnavailable || errorMessage(error), true);
    }
  }

  function initializeAll(scope) {
    const container = scope || document;
    container.querySelectorAll("[data-zoom-experience]").forEach(initialize);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { initializeAll(document); }, { once: true });
  } else {
    initializeAll(document);
  }

  if (!window.zoomExperienceSectionListener) {
    window.zoomExperienceSectionListener = true;
    document.addEventListener("shopify:section:load", function (event) { initializeAll(event.target); });
    window.addEventListener("pageshow", function (event) {
      if (!event.persisted) return;
      document.querySelectorAll('[data-zoom-experience][data-mode="broadcast_viewer"]').forEach(function (root) {
        root.dataset.zoomInitialized = "false";
        initialize(root);
      });
    });
  }
})();
